import { useQuery } from '@tanstack/react-query'

import { apiClient } from '../apiClient'

const GET_ALL_PROGRAM_KEY = 'operators-programs-key'

type ProgramListParams = {
  operatorIds?: string[]
  excludeTrainingProgramId?: string
}

type Response = {
  operators: Operator[]
  programs: Program[]
  trainingProgress?: TrainingProgress[]
}

export type ProgramListSuspensionTrigger = {
  count: number
  threshold: number
  window: string
}

export type ProgramListSuspensionRequalification = {
  moduleId: string
  status: 'not_started' | 'in_progress' | 'passed'
  attempts: number
  maxAttempts: number | null
  lastAttemptAt: string | null
  exhausted: boolean
}

export type ProgramListInstanceSuspension = {
  id: string
  origin: 'manual' | 'automated'
  startedAt: string
  trigger: ProgramListSuspensionTrigger | null
  categories: string[]
  requalification: ProgramListSuspensionRequalification | null
}

export type ProgramListCriticalErrors = {
  count: number
  threshold: number
  window: string
  approaching: boolean
}

export type ProgramListProgramInstance = {
  programInstanceId?: string | null
  programInstanceName?: string | null
  programGroupId?: string | null
  programGroupName?: string | null
  liveProgramId?: string | null
  recyclerProgramId?: string | null
  superMajorityProgramId?: string | null
  recyclerTrainingProgramId?: string | null
  displayName?: string | null
  color?: string | null
  iconType?: string | null
  liveEligible?: boolean | null
  recyclerEligible?: boolean | null
  superMajorityEligible?: boolean | null
  recyclerTrainingAssigned?: boolean | null
  recyclerTrainingProgress?: number | null
  recyclerTrainingCertified?: boolean | null
  recyclerTrainingCompleted?: boolean | null
  paused?: boolean | null
  liveAccuracy?: number | null
  recyclerAccuracy?: number | null
  status?: string | null
  livePaused?: boolean | null
  recyclerPaused?: boolean | null
  suspended?: boolean | null
  suspensionStartedAt?: string | null
  suspension?: ProgramListInstanceSuspension | null
  criticalErrors?: ProgramListCriticalErrors | null
  liveProgram?: ProgramListProgramInstanceProgram
  recyclerProgram?: ProgramListProgramInstanceProgram
}

export type ProgramListProgramInstanceProgram = {
  programId: string | null
  displayName?: string | null
  color?: string | null
  iconType?: string | null
  targetAccuracy?: number | null
  accuracy?: number | null
}

type Operator = {
  name: string | null
  id: string | null
  avatarUrl: string | null
  programIds: string[]
  trainingProgramIds: string[]
  certifiedTrainingProgramIds: string[]
  pausedProgramIds?: string[]
  isFullTime: boolean
  isWorkforceManaged: boolean
  programInstances?: ProgramListProgramInstance[]
}

type Program = {
  programId: string
  displayName: string | null
  color: string
  iconType: string | null
  targetAccuracy: number
  accuracy?: number | null
}

type TrainingProgress = {
  programId: string
  progress: number
}

export function useGetProgramList(params: ProgramListParams) {
  return useQuery({
    queryKey: [GET_ALL_PROGRAM_KEY, params],
    queryFn: async () => {
      const { data } = await apiClient.get<Response>(`/int/v3/operators/program-list`, {
        params,
      })

      return data
    },
    refetchOnWindowFocus: false,
    enabled: Boolean(
      params.excludeTrainingProgramId || (params.operatorIds && params.operatorIds.length > 0)
    ),
  })
}
